import React from 'react';

import { Box, CircularProgress, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip as MuiTooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';

const useStyles = makeStyles(() => ({
  chartContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    padding: '20px',
  },
  chartTitle: {
    alignItems: 'center',
    borderBottom: '1px solid #eaeaea',
    display: 'flex',
    fontSize: 18,
    fontWeight: 600,
    marginBottom: '20px',
    paddingBottom: '10px',
  },
  colorIndicator: {
    borderRadius: 3,
    display: 'inline-block',
    height: 12,
    marginRight: 8,
    width: 12,
  },
  emptyState: {
    alignItems: 'center',
    color: '#999',
    display: 'flex',
    fontSize: 14,
    height: 400,
    justifyContent: 'center',
  },
  errorState: {
    alignItems: 'center',
    color: '#d32f2f',
    display: 'flex',
    flexDirection: 'column',
    fontSize: 14,
    gap: 10,
    height: 400,
    justifyContent: 'center',
  },
  loadingContainer: {
    alignItems: 'center',
    display: 'flex',
    height: 400,
    justifyContent: 'center',
  },
  nameCell: {
    color: '#2196f3',
    fontWeight: 500,
  },
  performerItem: {
    color: '#666',
    fontSize: 12,
    marginBottom: 4,
  },
  performerLink: {
    '&:hover': {
      textDecoration: 'underline',
    },
    color: '#2196f3',
    cursor: 'pointer',
    textDecoration: 'none',
  },
  performerSection: {
    marginBottom: 15,
  },
  performerTitle: {
    color: '#172B4D',
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 8,
  },
  performersList: {
    borderTop: '1px solid #eaeaea',
    marginTop: 20,
    paddingTop: 15,
  },
  rankCell: {
    color: '#666',
    fontWeight: 600,
  },
  tableCell: {
    border: 'none',
    fontSize: 12,
    padding: '8px 16px',
  },
  tableContainer: {
    marginTop: 20,
  },
  tableHeaderCell: {
    border: 'none',
    color: '#172B4D',
    fontSize: 13,
    fontWeight: 600,
    padding: '12px 16px',
  },
  tableLink: {
    color: 'inherit',
    display: 'table-row',
    textDecoration: 'none',
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#f5f5f5',
      cursor: 'pointer',
    },
    textDecoration: 'none',
  },
}));

const TechnologistComparisonChart = ({ data, endDate, error, highlightTechnologistId, hideTables, loading, onTechnologistClick, startDate }) => {
  const classes = useStyles();

  if (loading) {
    return (
      <Box className={classes.chartContainer}>
        <Box className={classes.chartTitle}>
          <Typography>Technologist Comparison - Observations per FTE</Typography>
        </Box>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.chartContainer}>
        <Box className={classes.chartTitle}>
          <Typography>Technologist Comparison - Observations per FTE</Typography>
        </Box>
        <Box className={classes.errorState}>
          <Typography>Failed to load technologist comparison data</Typography>
          <Typography variant="caption">{error}</Typography>
        </Box>
      </Box>
    );
  }

  if (!data || !data.data || data.data.length === 0) {
    return (
      <Box className={classes.chartContainer}>
        <Box className={classes.chartTitle}>
          <Typography>Technologist Comparison - Observations per FTE</Typography>
        </Box>
        <Box className={classes.emptyState}>
          <Typography>No technologist data found for this period</Typography>
        </Box>
      </Box>
    );
  }

  const chartData = data.data.map((item) => ({
    fullTimeEmployment: item.fullTimeEmployment,
    name: item.name,
    observationCount: item.observationCount,
    observationsPerFTE: item.observationsPerFTE,
    technologistId: item.technologistId,
  }));

  const getBarColor = (value, techId) => {
    // If highlighting a specific technologist, color only that one
    if (highlightTechnologistId) {
      if (techId === highlightTechnologistId) {
        // Color the highlighted technologist based on their performance
        if (value <= data.bottomThreshold) {
          return '#4CAF50'; // Green for bottom 5%
        }
        if (value >= data.topThreshold) {
          return '#FF9800'; // Orange for top 5%
        }
        return '#FFC107'; // Yellow for middle
      }
      // All others are gray
      return '#CCCCCC';
    }

    // Default coloring when not highlighting
    if (value <= data.bottomThreshold) {
      return '#4CAF50'; // Green for bottom 5%
    }
    if (value >= data.topThreshold) {
      return '#FF9800'; // Orange for top 5%
    }
    return '#FFC107'; // Yellow for middle
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          style={{
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '10px',
          }}>
          <Typography style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{data.name}</Typography>
          <Typography style={{ fontSize: 11 }}>Total Observations: {data.observationCount}</Typography>
          <Typography style={{ fontSize: 11 }}>FTE: {data.fullTimeEmployment}</Typography>
          <Typography style={{ color: '#2196f3', fontSize: 11, fontWeight: 600 }}>Observations per FTE: {data.observationsPerFTE}</Typography>
        </Box>
      );
    }
    return null;
  };

  const handleBarClick = (data) => {
    if (data && data.technologistId) {
      window.open(`/technologist-summary/${data.technologistId}`, '_blank', 'noopener,noreferrer');
    }
  };

  // Calculate dynamic height based on number of technologists
  // Cap the height for very large datasets to maintain usability
  const barHeight = 30;
  const minHeight = 300;
  const maxHeight = 2000; // Maximum height to prevent extremely tall charts
  const calculatedHeight = Math.min(maxHeight, Math.max(minHeight, chartData.length * barHeight + 100));

  // For large datasets, hide Y-axis labels and rely on tooltips
  const showYAxisLabels = chartData.length <= 50;
  const yAxisWidth = showYAxisLabels ? 200 : 10;

  // Check if thresholds are close to each other (within 10% of the range)
  const thresholdDifference = Math.abs(data.topThreshold - data.bottomThreshold);
  const dataRange = Math.max(...chartData.map((d) => d.observationsPerFTE)) - Math.min(...chartData.map((d) => d.observationsPerFTE));
  const thresholdsAreClose = dataRange > 0 && thresholdDifference / dataRange < 0.1;

  // Adjust label offset only when thresholds are close
  const bottomLabelOffset = thresholdsAreClose ? 20 : 10;

  return (
    <Box className={classes.chartContainer}>
      <Box className={classes.chartTitle}>
        <Typography>
          Technologist Comparison (less is better)
          {startDate && endDate && (
            <Typography component="span" variant="caption" style={{ color: '#666', fontWeight: 400, marginLeft: 8 }}>
              ({startDate} - {endDate})
            </Typography>
          )}
          {chartData.length > 50 && (
            <Typography component="span" variant="caption" style={{ color: '#999', display: 'block', fontSize: 11, fontWeight: 400, marginTop: 4 }}>
              Showing {chartData.length} technologists with observations. Hover over bars for details.
            </Typography>
          )}
        </Typography>
      </Box>

      {/* Chart Section - Full Width */}
      <Box style={{ height: calculatedHeight, marginTop: 20, overflowY: chartData.length > 60 ? 'auto' : 'visible', position: 'relative' }}>
        <ResponsiveContainer width="100%" height={calculatedHeight}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{
              bottom: 20,
              left: -40,
              right: 40,
              top: 30,
            }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" label={{ offset: -10, position: 'insideBottom', style: { fontSize: 12 }, value: 'Observations per FTE' }} />
            <YAxis type="category" dataKey="name" width={yAxisWidth} style={{ fontSize: 14 }} interval={0} tick={showYAxisLabels ? undefined : false} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.05)' }} />
            <ReferenceLine
              x={data.bottomThreshold}
              stroke="#4CAF50"
              strokeDasharray="3 3"
              label={{ fill: '#4CAF50', fontSize: 10, offset: bottomLabelOffset, position: 'top', value: 'Bottom 5%' }}
            />
            <ReferenceLine x={data.topThreshold} stroke="#FF9800" strokeDasharray="3 3" label={{ fill: '#FF9800', fontSize: 10, offset: 10, position: 'top', value: 'Top 5%' }} />
            <Bar dataKey="observationsPerFTE" onClick={handleBarClick} style={{ cursor: 'pointer' }} barSize={20}>
              {chartData.map((entry) => (
                <Cell key={`cell-${Math.random()}`} fill={getBarColor(entry.observationsPerFTE, entry.technologistId)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {/* Only show tables if there's more than 1 technologist with observations and hideTables is not set */}
      {!hideTables && chartData.length > 1 && (
        <Box display="flex" flexDirection="row" width="100%" gridGap="40px" marginTop="20px" justifyContent="center" flexWrap="wrap">
          {/* Bottom Performers Table (Fewest errors) */}
          <Box width="600px">
            {data.bottomPerformers && data.bottomPerformers.length > 0 && (
              <>
                <Typography style={{ backgroundColor: '#f3f3f3', borderRadius: 6, color: 'black', fontSize: 16, fontWeight: 600, marginBottom: 10, padding: '10px 20px' }}>
                  Technologists with fewest errors/FTE (below {data.bottomThreshold.toFixed(2)})
                </Typography>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.tableHeaderCell} style={{ width: 20 }} align="center">
                          #
                        </TableCell>
                        <TableCell className={classes.tableHeaderCell} style={{ width: 20 }} />
                        <TableCell className={classes.tableHeaderCell}>Name</TableCell>
                        <TableCell className={classes.tableHeaderCell} align="center">
                          Observations
                        </TableCell>
                        <TableCell className={classes.tableHeaderCell} align="center">
                          FTE
                        </TableCell>
                        <TableCell className={classes.tableHeaderCell} align="center">
                          Obs/FTE
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.bottomPerformers.map((tech, index) => {
                        const barColor = getBarColor(tech.observationsPerFTE, tech.technologistId);
                        return (
                          <MuiTooltip key={tech.technologistId} title="Click to see technologist summary (opens in new tab)" placement="top">
                            <TableRow className={classes.tableRow} component="a" href={`/technologist-summary/${tech.technologistId}`} target="_blank" rel="noopener noreferrer">
                              <TableCell className={`${classes.tableCell} ${classes.rankCell}`} align="center">
                                {index + 1}
                              </TableCell>
                              <TableCell className={classes.tableCell} align="center">
                                <Box className={classes.colorIndicator} style={{ backgroundColor: barColor }} />
                              </TableCell>
                              <TableCell className={`${classes.tableCell} ${classes.nameCell}`}>{tech.name}</TableCell>
                              <TableCell className={classes.tableCell} align="center">
                                {tech.observationCount}
                              </TableCell>
                              <TableCell className={classes.tableCell} align="center">
                                {tech.fullTimeEmployment}
                              </TableCell>
                              <TableCell className={classes.tableCell} align="center" style={{ fontWeight: 600 }}>
                                {tech.observationsPerFTE}
                              </TableCell>
                            </TableRow>
                          </MuiTooltip>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>

          {/* Top Performers Table (Most errors) */}
          <Box width="600px">
            {data.topPerformers && data.topPerformers.length > 0 && (
              <>
                <Typography style={{ backgroundColor: '#f3f3f3', borderRadius: 6, color: 'black', fontSize: 16, fontWeight: 600, marginBottom: 10, padding: '10px 20px' }}>
                  Technologists with most errors/FTE (above {data.topThreshold.toFixed(2)})
                </Typography>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.tableHeaderCell} style={{ width: 30 }} align="center">
                          #
                        </TableCell>
                        <TableCell className={classes.tableHeaderCell} style={{ width: 30 }} />
                        <TableCell className={classes.tableHeaderCell}>Name</TableCell>
                        <TableCell className={classes.tableHeaderCell} align="center">
                          Observations
                        </TableCell>
                        <TableCell className={classes.tableHeaderCell} align="center">
                          FTE
                        </TableCell>
                        <TableCell className={classes.tableHeaderCell} align="center">
                          Obs/FTE
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {data.topPerformers.map((tech, index) => {
                        const barColor = getBarColor(tech.observationsPerFTE, tech.technologistId);

                        return (
                          <MuiTooltip key={tech.technologistId} title="Click to see technologist summary" placement="top">
                            <TableRow className={classes.tableRow} component="a" href={`/technologist-summary/${tech.technologistId}`} target="_blank" rel="noopener noreferrer">
                              <TableCell className={`${classes.tableCell} ${classes.rankCell}`} align="center">
                                {index + 1}
                              </TableCell>
                              <TableCell className={classes.tableCell} align="center">
                                <Box className={classes.colorIndicator} style={{ backgroundColor: barColor }} />
                              </TableCell>
                              <TableCell className={`${classes.tableCell} ${classes.nameCell}`}>{tech.name}</TableCell>
                              <TableCell className={classes.tableCell} align="center">
                                {tech.observationCount}
                              </TableCell>
                              <TableCell className={classes.tableCell} align="center">
                                {tech.fullTimeEmployment}
                              </TableCell>
                              <TableCell className={classes.tableCell} align="center" style={{ fontWeight: 600 }}>
                                {tech.observationsPerFTE}
                              </TableCell>
                            </TableRow>
                          </MuiTooltip>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TechnologistComparisonChart;
